/**
 * Phase 4 — inline re-import dialog used by both Run and RunDetail pages
 * when a run lands in `cookie_expired` / `failed` state. Submits to the
 * existing `/api/settings/cookies` + `/api/settings/verify` endpoints; on
 * a clean verify the dialog auto-closes and the parent's `onVerified`
 * callback fires (typically to enable the "Resume Run" button).
 */
import { useState } from "react";
import { toast } from "sonner";
import { api, detailFromError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function CookieReimportDialog({ open, onOpenChange, onVerified }) {
  const [cookieJson, setCookieJson] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    let parsed;
    try {
      parsed = JSON.parse(cookieJson);
    } catch {
      toast.error("Invalid JSON. Paste the full Cookie-Editor export.");
      return;
    }
    if (!Array.isArray(parsed)) {
      toast.error("Expected a JSON array of cookies.");
      return;
    }
    setBusy(true);
    try {
      const importRes = await api.post("/settings/cookies", { cookies: parsed });
      toast.success(
        `${importRes.data.imported_count} cookies imported (${importRes.data.amazon_in_count} for amazon.in)`
      );
      const verifyRes = await api.post("/settings/verify");
      const result = verifyRes.data?.result;
      if (result === "on_orders") {
        toast.success("Session verified — logged in");
        setCookieJson("");
        onOpenChange(false);
        if (onVerified) onVerified(verifyRes.data);
      } else {
        toast.error(`Verify came back: ${result || "unknown"} — try fresh cookies.`);
      }
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="cookie-reimport-dialog">
        <DialogHeader>
          <DialogTitle>Re-import cookies</DialogTitle>
          <DialogDescription>
            Paste a fresh Cookie-Editor JSON export from your amazon.in tab. The session
            will be verified before this dialog closes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="reimport-cookies-textarea">Cookie JSON</Label>
          <Textarea
            id="reimport-cookies-textarea"
            data-testid="reimport-cookies-textarea"
            value={cookieJson}
            onChange={(e) => setCookieJson(e.target.value)}
            placeholder='[{"domain":".amazon.in","name":"session-id","value":"...", ...}, ...]'
            className="font-mono text-xs min-h-[200px]"
          />
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="cancel-reimport-button"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !cookieJson.trim()}
            data-testid="submit-reimport-button"
          >
            {busy ? "Importing & verifying…" : "Import & verify"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
